import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';

import { logout } from '../../auth';
import { get, post } from '../../services/api';
import { formatarOpcaoFilial } from '../../utils/filial';
import AdminSection from '../AdminSection/AdminSection';
import AdminTable from '../AdminTable/AdminTable';
import {
    buildLinhaRemuneracaoVendedor,
    getColunasRemuneracaoVendedores,
    getLinhasPaginadas,
    getOpcoesPeriodo,
    getTotalPaginas,
    LINHAS_POR_PAGINA
} from './ConsultaRemuneracaoVendedores.utils';

import './ConsultaRemuneracaoVendedores.css';

function getResponseErrorMessage(response, fallback) {
    return response?.response?.data?.Mensagem
        || response?.response?.data?.Error
        || response?.Mensagem
        || response?.message
        || fallback;
}

export default function ConsultaRemuneracaoVendedores({ setNotifier }) {
    const navigate = useNavigate();
    const optionsPeriodo = useMemo(() => getOpcoesPeriodo(), []);
    const selectStyle = useMemo(() => ({
        control: (base, state) => ({
            ...base,
            background: 'unset',
            borderColor: '#DCDCDC',
            borderRadius: '4px',
            boxShadow: state.isFocused ? '0 0 0 1px #2684FF' : 'none',
            minHeight: '38px'
        }),
        valueContainer: base => ({ ...base, padding: '0 8px' }),
        indicatorsContainer: base => ({ ...base, height: '38px' }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    }), []);

    const [authToken, setAuthToken] = useState(null);
    const [optionsFilial, setOptionsFilial] = useState([]);
    const [optionsCluster, setOptionsCluster] = useState([]);
    const [filialSelecionada, setFilialSelecionada] = useState(null);
    const [nomeVendedor, setNomeVendedor] = useState('');
    const [periodoSelecionado, setPeriodoSelecionado] = useState(
        optionsPeriodo.find(option => option.isCurrent)
    );
    const [vendedores, setVendedores] = useState(null);
    const [editandoVendedor, setEditandoVendedor] = useState(null);
    const [clusterEditado, setClusterEditado] = useState(null);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    async function buscarDados() {
        try {
            setIsLoading(true);
            const params = new URLSearchParams();
            params.append('idfiliais', filialSelecionada?.value || 0);
            params.append('mes', periodoSelecionado.mes);
            params.append('ano', periodoSelecionado.ano);

            if (nomeVendedor.trim()) {
                params.append('nome_vendedor', nomeVendedor.trim());
            }

            const response = await get(
                `/admin/remuneracao/vendedores?${params.toString()}`,
                null,
                authToken
            );

            if (!Array.isArray(response)) {
                setNotifier(false, 'Nao foi possivel consultar os vendedores.');
                setVendedores([]);
                return;
            }

            setVendedores(response);
            setEditandoVendedor(null);
            setClusterEditado(null);
            setPage(1);
        } finally {
            setIsLoading(false);
        }
    }

    function iniciarEdicao(vendedor) {
        setEditandoVendedor(vendedor.idvendedor);
        setClusterEditado(
            optionsCluster.find(option => String(option.value) === String(vendedor.idcluster))
            || null
        );
    }

    function cancelarEdicao() {
        setEditandoVendedor(null);
        setClusterEditado(null);
    }

    async function salvarCluster(vendedor) {
        if (!clusterEditado) {
            setNotifier(false, 'Selecione um cluster.');
            return;
        }

        const response = await post('/admin/remuneracao/vendedores/cluster', {
            idvendedor: vendedor.idvendedor,
            idcluster: clusterEditado.value,
            mes: periodoSelecionado.mes,
            ano: periodoSelecionado.ano,
            idusuario: localStorage.getItem('idusuario')
        }, authToken);

        if (!response?.Sucesso) {
            setNotifier(
                false,
                getResponseErrorMessage(response, 'Erro ao atualizar o cluster.')
            );
            return;
        }

        setNotifier(true, response.Mensagem);
        await buscarDados();
    }

    const dados = Array.isArray(vendedores)
        ? vendedores.map(vendedor => {
            const linha = buildLinhaRemuneracaoVendedor(vendedor);
            const isEditing = editandoVendedor === vendedor.idvendedor;

            return {
                ...linha,
                cluster: isEditing ? (
                    <Select
                        className="react-select cluster-select-inline"
                        classNamePrefix="react-select"
                        options={optionsCluster}
                        value={clusterEditado}
                        onChange={setClusterEditado}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyle}
                        isSearchable={false}
                        placeholder="Cluster"
                    />
                ) : linha.cluster,
                acao: isEditing ? (
                    <div className="action-buttons">
                        <button
                            type="button"
                            className="action-btn"
                            aria-label={`Confirmar cluster do vendedor ${vendedor.idvendedor}`}
                            onClick={() => salvarCluster(vendedor)}
                        >
                            <CheckCircleIcon className="confirm-icon" />
                        </button>
                        <button
                            type="button"
                            className="action-btn"
                            aria-label={`Cancelar edicao do vendedor ${vendedor.idvendedor}`}
                            onClick={cancelarEdicao}
                        >
                            <CancelIcon className="cancel-icon" />
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        className="action-btn edit-btn"
                        aria-label={`Editar cluster do vendedor ${vendedor.idvendedor}`}
                        onClick={() => iniciarEdicao(vendedor)}
                    >
                        <EditIcon />
                    </button>
                )
            };
        })
        : null;

    const dadosPaginados = getLinhasPaginadas(dados, page);
    const numberOfPages = getTotalPaginas(dados);

    useEffect(() => {
        const currentAuthToken = localStorage.getItem('adminToken');

        if (!currentAuthToken) {
            logout();
            navigate('/login');
            return;
        }

        setAuthToken(currentAuthToken);

        async function loadOptions() {
            const [filiais, clusters] = await Promise.all([
                get('/admin/metas/filial', null, currentAuthToken),
                get('/admin/metas/cluster', null, currentAuthToken)
            ]);

            const flatFiliais = [];
            if (Array.isArray(filiais)) {
                filiais.forEach(group => {
                    const items = group.items || group.options;
                    if (Array.isArray(items)) {
                        flatFiliais.push(...items);
                    } else if (group?.value !== undefined) {
                        flatFiliais.push(group);
                    }
                });
            }

            flatFiliais.sort((a, b) => Number(a.value) - Number(b.value));
            const optionsFormatadas = flatFiliais.map(formatarOpcaoFilial);
            setOptionsFilial([{ label: 'Todas', value: 0 }, ...optionsFormatadas]);

            if (Array.isArray(clusters)) {
                setOptionsCluster(clusters.map(cluster => ({
                    label: String(cluster.id_cluster),
                    value: cluster.id_cluster
                })));
            }
        }

        loadOptions();
    }, [navigate]);

    return (
        <div className="remuneracao-vendedores-page">
            <AdminSection
                etapa={1}
                titulo="Consultar cluster"
                acoes={(
                    <button
                        type="button"
                        className="upload-btn"
                        onClick={() => navigate('/admin/remuneracao/vendedores/importacao')}
                    >
                        Upload
                    </button>
                )}
            >
                <div className="consulta-remuneracao-filtros">
                    <div className="filtro-item">
                        <label htmlFor="filial">Filial</label>
                        <Select
                            inputId="filial"
                            className="react-select"
                            classNamePrefix="react-select"
                            options={optionsFilial}
                            value={filialSelecionada}
                            onChange={setFilialSelecionada}
                            placeholder="Todas"
                            menuPortalTarget={document.body}
                            styles={selectStyle}
                            noOptionsMessage={() => 'Nenhuma opcao disponivel'}
                        />
                    </div>

                    <div className="filtro-item">
                        <label htmlFor="nomeVendedor">Nome vendedor</label>
                        <input
                            id="nomeVendedor"
                            type="text"
                            placeholder="Todos"
                            value={nomeVendedor}
                            onChange={event => setNomeVendedor(event.target.value)}
                        />
                    </div>

                    <div className="filtro-item">
                        <label htmlFor="periodo">Período</label>
                        <Select
                            inputId="periodo"
                            className="react-select"
                            classNamePrefix="react-select"
                            options={optionsPeriodo}
                            value={periodoSelecionado}
                            onChange={setPeriodoSelecionado}
                            menuPortalTarget={document.body}
                            styles={selectStyle}
                            isSearchable={false}
                        />
                    </div>

                    <div className="filtro-item filtro-botao">
                        <button
                            type="button"
                            className="search-btn"
                            onClick={buscarDados}
                            disabled={isLoading || !authToken}
                        >
                            {isLoading ? 'Filtrando...' : 'Filtrar'}
                        </button>
                    </div>
                </div>
            </AdminSection>

            <AdminSection etapa={2} titulo="Consultar vendedores">
                <AdminTable
                    atributoKey="idvendedor"
                    colunas={getColunasRemuneracaoVendedores()}
                    dados={dadosPaginados}
                    styleName="tabela-remuneracao-vendedores"
                    isPaginada={dados?.length > LINHAS_POR_PAGINA}
                    page={page}
                    setPage={setPage}
                    numberOfPages={numberOfPages}
                    setDadosByPage={setPage}
                />
            </AdminSection>
        </div>
    );
}
